// Live Letterboxd enrichment for a public taste page: the four curated
// favourites and a filmstrip of recent watches, scraped + edge-cached by the
// worker (/api/lb/:username). Unlike the static export snapshot, this is fresh —
// it shows what they've logged SINCE the upload. Best-effort: renders nothing
// (no Tears, no headings) until data arrives, and stays hidden if the scrape
// came back empty, so the page is unchanged when Letterboxd is unreachable.

import { useEffect, useMemo, useState } from "react";
import { Tear, Field, Kicker, H3, Sub } from "./sheet.tsx";
import { PosterCard } from "./brand.tsx";
import { usePosters } from "../lib/usePosters.ts";
import { getLetterboxdActivity, type LbActivity } from "../lib/api.ts";

function watchedLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function LetterboxdActivity({
  username,
  name,
}: {
  username: string;
  name: string;
}) {
  const [act, setAct] = useState<LbActivity | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLetterboxdActivity(username).then((a) => {
      if (!cancelled) setAct(a);
    });
    return () => {
      cancelled = true;
    };
  }, [username]);

  // Favourites carry no poster in the markup (lazy-loaded), so resolve them
  // through the same TMDB path the rest of the page uses.
  const favFilms = useMemo(
    () =>
      (act?.favorites ?? []).map((f) => ({
        uri: `lbfav:${f.slug}`,
        title: f.title,
        year: f.year ?? undefined,
      })),
    [act],
  );
  const favPosters = usePosters(favFilms);

  if (!act || (!act.favorites.length && !act.recent.length)) return null;

  return (
    <div data-testid="letterboxd-activity" className="lbfade">
      {act.favorites.length > 0 && (
        <>
          <Tear />
          <Field testid="section-lb-favorites">
            <Kicker>Four favourites</Kicker>
            <H3>{name}'s permanent four</H3>
            <Sub>
              The films pinned to the top of their Letterboxd — the taste they
              lead with.
            </Sub>
            <div className="mt-4 grid grid-cols-4 gap-3 sm:gap-4">
              {act.favorites.map((f) => (
                <a
                  key={f.slug}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline"
                  aria-label={`${f.title} on Letterboxd`}
                >
                  <PosterCard
                    title={f.title}
                    year={f.year ?? undefined}
                    poster={favPosters[`lbfav:${f.slug}`]}
                    width="100%"
                  />
                </a>
              ))}
            </div>
          </Field>
        </>
      )}

      {act.recent.length > 0 && (
        <>
          <Tear />
          <Field testid="section-lb-recent">
            <Kicker>Fresh from Letterboxd</Kicker>
            <H3>Recently watched</H3>
            <Sub>
              Their latest logs, live from{" "}
              <a
                href={`https://letterboxd.com/${username}/films/diary/`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                letterboxd.com/{username}
              </a>{" "}
              — updated well after the snapshot above.
            </Sub>
            <div className="lbstrip mt-4 flex gap-3 overflow-x-auto pb-2 sm:gap-4">
              {act.recent.map((e, i) => (
                <a
                  key={`${e.url}-${i}`}
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[104px] shrink-0 no-underline sm:w-[116px]"
                  aria-label={`${e.title} on Letterboxd`}
                >
                  <PosterCard
                    title={e.title}
                    year={e.year ?? undefined}
                    poster={e.poster}
                    rating={e.rating ?? undefined}
                    badge={e.rewatch ? "Rewatch" : undefined}
                    width="100%"
                  />
                  {e.watchedDate && (
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-stamp text-ink-400">
                      {watchedLabel(e.watchedDate)}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </Field>
        </>
      )}
    </div>
  );
}
